import React, { useState, useEffect, useRef } from 'react';

import { UserRepository } from '../../MedicareProAPI/Repositories/User/UserRepository';
import { UserLetterRepository } from '../../MedicareProAPI/Repositories/User/UserLetterRepository';
import { StaffRepository } from '../../MedicareProAPI/Repositories/Staff/StaffRepository';
import { AgencyDownlineRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyDownlineRepository';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import StyledModal from './StyledModal';
import RichTextEditor from './Form/Field/RichTextEditor';
import { FileUpload } from './Form/Field/FileUpload';

export default function ProfileNotesLogsEmailsMultiple({
  ind,
  userAccessToken,
  userId,
  staffId,
  agency,
  onIntegrationSetupClick,
}) {
  const userRepository = new UserRepository();
  userRepository.setAccessToken(userAccessToken);

  const userLetterRepository = new UserLetterRepository();
  userLetterRepository.setAccessToken(userAccessToken);

  const staffRepository = new StaffRepository();
  staffRepository.setAccessToken(userAccessToken);

  const agencyDownlineRepository = new AgencyDownlineRepository();
  agencyDownlineRepository.setAccessToken(userAccessToken);

  let indEmails = [];
  if (ind.getEmails()) {
    ind
      .getEmails()
      .getItems()
      .forEach((indEmail) => indEmails.push(indEmail.getIndEmailAddress()));
  }

  if (ind.getSpouse() && ind.getSpouse().getEmails()) {
    ind
      .getSpouse()
      .getEmails()
      .getItems()
      .forEach((indEmail) => indEmails.push(indEmail.getIndEmailAddress()));
  }

  const [mailPlatform, setMailPlatform] = useState(undefined);
  const [emailAddress, setEmailAddress] = useState(
    ind.getAnyEmail()
      ? ind.getAnyEmail().getIndEmailAddress()
      : ind.getSpouse() && ind.getSpouse().getAnyEmail()
        ? ind.getSpouse().getAnyEmail().getIndEmailAddress()
        : null
  );
  const [emailAddresses, setEmailAddresses] = useState(indEmails);

  const [mailThreadID, setMailThreadID] = useState(null);
  const [mailThreads, setMailThreads] = useState(null);
  const [mailThreadsLoading, setMailThreadsLoading] = useState(false);
  const [mailThreadsErrorMessage, setMailThreadsErrorMessage] = useState(false);

  const [mailMessageID, setMailMessageID] = useState(null);
  const [mailMessages, setMailMessages] = useState(null);
  const [mailMessagesLoading, setMailMessagesLoading] = useState(null);

  const [savingAttachment, setSavingAttachment] = useState(null);

  const composeMessageCCElement = useRef();
  const [composeMessageModal, setComposeMessageModal] = useState(false);
  const [composeMessage, setComposeMessage] = useState(null);
  const [composeMessageTo, setComposeMessageTo] = useState(null);
  const [composeMessageCC, setComposeMessageCC] = useState(null);
  const [composeMessageSubject, setComposeMessageSubject] = useState(null);
  const [composeMessageBody, setComposeMessageBody] = useState(null);
  const [composeMessageSending, setComposeMessageSending] = useState(null);
  const [composeMessageSent, setComposeMessageSent] = useState(false);

  const [userEmailTemplates, setUserEmailTemplates] = useState(null);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);
  const [userEmailSignature, setUserEmailSignature] = useState(null);
  const [staffEmailSignature, setStaffEmailSignature] = useState(null);
  const [user, setUser] = useState(null);
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [changingTargetUser, setChangingTargetUser] = useState(false);

  const [targetUser, setTargetUser] = useState(null);
  const [targetUserName, setTargetUserName] = useState(null);

  const [externalData, setExternalData] = useState(null);
  const [attachments, setAttachments] = useState([]);

  var webAPI = new MedicareProAPI.WebAPI();
  webAPI.setAccessToken(userAccessToken);

  /**
   * Handles a successful file upload by appending it to the attachments list,
   * updating the state, and triggering a save with the new attachment included.
   *
   * @param fileData - The uploaded file response containing metadata
   */
  const handleFileUpload = (fileData) => {
    const newAttachment = {
      systemName: fileData.name,
      fileName: fileData.fileName,
      key: fileData.key,
    };

    const updatedAttachments = [...attachments, newAttachment];
    setAttachments(updatedAttachments);
  };

  /**
   * Removes an attachment from the list by index,
   * updates the state, and triggers a save with the updated list.
   *
   * @param attachmentIndex - The index of the attachment to remove
   */
  const handleRemoveAttachment = (attachmentIndex) => {
    const updatedAttachments = attachments.filter((_, i) => i !== attachmentIndex);
    setAttachments(updatedAttachments);
  };

  // Expose global callback so we can use this component as sender for legacy email operations, i.e. sending quote data
  useEffect(() => {
    window.externalEmailLoad = (payload) => {
      setExternalData(payload);
      setComposeMessageModal(true);

      if (payload?.subject) {
        setComposeMessageSubject(payload.subject);
      }
      if (payload?.body) {
        setComposeMessageBody(payload.body);
      }
    };
    return () => {
      delete window.externalEmailLoad;
    };
  }, []);

  const saveAttachment = (emailId, attachmentId) => {
    setSavingAttachment(true);

    webAPI
      .saveUserIntegrationMailAttachment(mailPlatform, emailId, attachmentId, {
        indId: ind.getIndId(),
      })
      .then((response) => {
        setSavingAttachment(false);
      });
  };

  const loadEmail = (emailId) => {
    setMailMessages(null);
    setMailMessagesLoading(true);
    setMailMessageID(null);
    setMailThreadID(emailId);

    webAPI
      .getUserIntegrationMail(targetUser, mailPlatform, {
        threadId: emailId,
        emails: emailAddresses,
      })
      .then((response) => {
        if (!response) {
          setMailMessages('notConnected');
        } else {
          setMailMessages(response);
          setMailMessagesLoading(false);
        }
      });
  };

  const loadCompose = (email = null, subject = null) => {
    setComposeMessageSubject(subject);
    //setComposeMessageBody(null);

    userEmailSignature ? setComposeMessageBody(userEmailSignature.getSignature()) : null;
    staffEmailSignature ? setComposeMessageBody(staffEmailSignature.getSignature()) : null;

    setComposeMessage(email);
    setComposeMessageModal(true);

    if (email) {
      if (email.replyTo && email.replyTo.length > 0) {
        setComposeMessageTo(Array.from(email.replyTo, (toEmail) => toEmail.email));
      } else if (email.from && email.from.email) {
        setComposeMessageTo([email.from.email]);
      }

      if (email.replyCC) {
        setComposeMessageCC(Array.from(email.replyCC, (ccEmail) => ccEmail.email));
      }
    }

    setComposeMessageSending(null);
    setComposeMessageSent(false);
  };

  const escapeHtml = (v) =>
    String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const buildAttachmentsHtml = (files) => {
    if (!Array.isArray(files) || files.length === 0) return '';

    const base = 'https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com';

    const rows = files
      .map((a) => {
        const key = encodeURIComponent(a?.key ?? '');
        const name = escapeHtml(a?.fileName ?? 'download');
        return `<div class="mt-2"><a href="${base}/${key}" target="_blank" rel="noopener noreferrer">${name}</a></div>`;
      })
      .join('');

    return `<div class="mt-3" style="margin-top:20px"><strong>Attachments</strong>${rows}</div>`;
  };

  const sendEmail = () => {
    setComposeMessageSending(true);
    const attachmentsHtml = buildAttachmentsHtml(attachments);

    const finalHtml = attachmentsHtml
      ? `${composeMessageBody}\n${attachmentsHtml}`
      : composeMessageBody;

    (!composeMessage
      ? webAPI.newUserIntegrationMail(mailPlatform, null, {
          subject: composeMessageSubject,
          message: finalHtml,
          to: [emailAddress],
          cc: composeMessageCC,
        })
      : webAPI.replyUserIntegrationMail(mailPlatform, composeMessage.id, null, {
          subject: composeMessageSubject,
          message: finalHtml,
          to: composeMessageTo,
          cc: composeMessageCC,
        })
    ).then((response) => {
      setComposeMessageSubject(null);
      setComposeMessageBody(null);
      setAttachments([]);
      setComposeMessageSending(false);
      setComposeMessageSent(true);
    });
  };

  const loadEmailTemplates = () => {
    if (targetUser) {
      userRepository
        .getUser(targetUser, {
          include: ['letters', 'signatureEmail'],
        })
        .then((templates) => {
          setUser(templates);
          setUserEmailTemplates(templates.getLetters());
          setUserEmailSignature(templates.getSignatureEmail());
        });
    }
  };

  const handleSelectedEmailTemplate = (event) => {
    const tpl = userEmailTemplates.getItem(event);

    setSelectedEmailTemplate(parseInt(event));
    const templatedBody = userEmailTemplates
      .getItem(parseInt(event))
      .renderLetterBody(
        ind,
        user,
        null,
        staffEmailSignature || userEmailSignature,
        null,
        null,
        false,
        null,
        'single'
      );

    // wiring in data from legacy js file
    if (externalData?.action === 'quote') {
      const sig =
        staffEmailSignature?.getSignature?.() ?? userEmailSignature?.getSignature?.() ?? '';
      const currentMessage = composeMessageBody;
      const newMessage = `${templatedBody ?? ''}${currentMessage ?? ''}${sig}`;
      setComposeMessageBody(newMessage);
    } else {
      const subject = (
        (tpl.getLetterSubject?.() ?? '').trim() ||
        tpl.getLetterTitle?.() ||
        ''
      ).trim();

      setComposeMessageSubject(subject);
      setComposeMessageBody(templatedBody);
    }
  };

  const handleDeleteCC = (address) => {
    let newCC = [...composeMessageCC];
    let currentCCIndex = newCC.indexOf(address);
    if (currentCCIndex > -1) {
      newCC.splice(currentCCIndex, 1);
    }

    setComposeMessageCC(newCC);
    composeMessageCCElement.current.focus();
  };

  const handleChangeCC = (event) => {
    let value = event.target.value;
    if (value && ((event.type === 'keyup' && event.which === 13) || event.type === 'blur')) {
      let emailValid = Utilities.validateEmail(value);

      if (emailValid) {
        if (composeMessageCC === null || !composeMessageCC.includes(value)) {
          let newCC = [];

          composeMessageCC ? (newCC = [...composeMessageCC]) : '';

          newCC.push(value);

          setComposeMessageCC(newCC);
        }

        event.target.value = '';
        event.target.focus();
      }
    }
  };

  const changeTargetUser = (targetUser) => {
    let thisTargetUser = authorizedUsers.filter((user) => {
      return user.getUser().getUserId() == targetUser;
    });

    setTargetUser(thisTargetUser[0].getUser().getUserId());
    setTargetUserName(
      thisTargetUser[0].getUser().getUserFirstname() +
        ' ' +
        thisTargetUser[0].getUser().getUserLastname()
    );
    setChangingTargetUser(true);
  };

  useEffect(() => {
    if (mailPlatform && emailAddresses && emailAddresses.length > 0 && targetUser) {
      setMailThreadsLoading(true);

      webAPI
        .getUserIntegrationMailThreads(targetUser, mailPlatform, { emails: emailAddresses })
        .then((response) => {
          if (response[0]) {
            loadEmail(response[0].threadId);
          }

          setMailThreadsLoading(false);
          setMailThreads(response.length === 0 ? null : response);
          setChangingTargetUser(false);
        })
        .catch((error) => {
          setMailThreadsLoading(false);
          setMailThreadsErrorMessage(error.message);
          setChangingTargetUser(false);
        });
    }
  }, [mailPlatform, targetUser]);

  useEffect(() => {
    if (targetUser) {
      webAPI.getUserIntegrationFeatureAvailability(targetUser, 'email').then((response) => {
        if (response[0]) {
          setMailPlatform(response[0].reference);

          if (staffId) {
            staffRepository
              .getUserStaff(targetUser, {
                include: ['staffEmailSignature'],
                criteria: { staff: staffId },
              })
              .then((staffResponse) => {
                if (staffResponse.getTotal() > 0) {
                  if (staffResponse.getItems()[0]['staffEmailSignature'] !== null) {
                    setStaffEmailSignature(staffResponse.getItems()[0].getSignatureEmail());
                  }
                }
              });
          }
        } else {
          setMailPlatform(null);
          setChangingTargetUser(false);
        }
      });
    }
  }, [targetUser]);

  useEffect(() => {
    if (parseInt(agency) > 0) {
      agencyDownlineRepository
        .getAgencyDownlines(userId, agency, { sortBy: 'downlineId', order: 'asc' })
        .then((response) => {
          if (response.getItems().length > 0) {
            const authorizedUsers = response.getItems().filter((item, itemIndex) => {
              return item.getAgentStatus() == 'LOA' || itemIndex === 0;
            });

            const thisTargetUser = authorizedUsers.filter((user) => {
              return user.getUser().getUserId() == userId;
            });

            setAuthorizedUsers(authorizedUsers);

            if (thisTargetUser && thisTargetUser.length > 0) {
              setTargetUser(thisTargetUser[0].getUser().getUserId());
              setTargetUserName(
                thisTargetUser[0].getUser().getUserFirstname() +
                  ' ' +
                  thisTargetUser[0].getUser().getUserLastname()
              );
            } else {
              setTargetUser(userId);
            }
          } else {
            setMailPlatform(null);
          }
        });
    } else {
      setTargetUser(userId);
    }
  }, []);

  return (
    <>
      <StyledModal
        size="lg"
        title={composeMessage ? 'Reply to Email' : 'Compose New Email'}
        footerCloseButton={true}
        show={composeMessageModal}
        onClose={(e) => {
          setComposeMessage(null);
          setComposeMessageTo(null);
          setComposeMessageCC(null);
          setComposeMessageSubject(null);
          setComposeMessageModal(false);
        }}
      >
        {composeMessageSent ? (
          <>
            <div className="alert small alert-success" role="alert">
              Your email was sent successfully
            </div>
          </>
        ) : (
          <>
            <div class="row">
              {authorizedUsers.length > 0 && (
                <div class="col-12 mt-0 ">
                  <div
                    className="badge badge-primary dataBadge float-right"
                    style={{ fontSize: '10pt' }}
                  >
                    <i className="far fa-inbox"></i> {targetUserName}
                  </div>
                </div>
              )}
              <div class="col-12 col-lg-6 mt-2">
                <label class="label">To</label>
                {composeMessage ? (
                  <div class="form-control border-secondary d-inline-flex flex-wrap mb-2 pl-2 form-control-tags pt-1">
                    {composeMessageTo.map((toAddress) => (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        class="badge bg-info text-white mt-0 mr-1 my-auto py-auto"
                      >
                        {toAddress}
                      </div>
                    ))}
                  </div>
                ) : (
                  <select
                    class="form-control form-control-sm"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                  >
                    {emailAddresses &&
                      emailAddresses.map((emailSingle) => (
                        <option value={emailSingle}>{emailSingle}</option>
                      ))}
                  </select>
                )}
              </div>
              <div class="col-12 col-lg-6 mt-2">
                <label class="label">CC</label>
                <div
                  onClick={(e) => composeMessageCCElement.current.focus()}
                  class="form-control border-secondary d-inline-flex flex-wrap mb-2 pl-2 form-control-tags pt-1"
                >
                  {composeMessageCC &&
                    composeMessageCC.map((ccAddress) => (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: '9pt' }}
                        class="badge bg-info text-white mt-0 mr-1 my-auto py-auto"
                      >
                        {ccAddress}{' '}
                        <i onClick={(e) => handleDeleteCC(ccAddress)} class="fa fa-trash ml-1"></i>
                      </div>
                    ))}

                  <input
                    type="text"
                    class="border-0 w-auto flex-fill input-tags"
                    autocomplete="off"
                    onKeyUp={handleChangeCC}
                    onBlur={handleChangeCC}
                    ref={composeMessageCCElement}
                    style={{ fontSize: '9pt' }}
                  />
                </div>
              </div>
              <div class="col-12 col-lg-6 mt-2">
                <label class="label">Templates</label>
                <div class="input-group input-group-sm mb-2">
                  <select
                    class="form-control form-control-sm"
                    onChange={(e) => handleSelectedEmailTemplate(e.target.value)}
                  >
                    <option>Select...</option>
                    {userEmailTemplates
                      ? userEmailTemplates.getItems().map((template, templateIndex) => {
                          return <option value={templateIndex}>{template.getLetterTitle()}</option>;
                        })
                      : loadEmailTemplates()}
                  </select>
                </div>
              </div>
            </div>
            <div>
              <label class="label">{composeMessage ? 'Replying to' : 'Subject'}</label>
              <div class="input-group input-group-sm mb-2">
                <input
                  type="text"
                  onChange={(e) => setComposeMessageSubject(e.target.value)}
                  class="form-control border-secondary"
                  value={composeMessage ? composeMessage.subject : composeMessageSubject}
                  disabled={composeMessage}
                  autocomplete="off"
                />
              </div>
            </div>
            <div class="mt-2">
              <label class="label">Message</label>
              <RichTextEditor
                value={composeMessageBody}
                onEditorChange={(e) => setComposeMessageBody(e)}
              />
            </div>
            {/* attachments */}

            <div className="mt-1 attachmentRow">
              <div className="dropdown" style={{ display: 'inline-block' }}>
                <div className="pt-3">
                  <div className="calMess attachTitle">
                    <b className="mb-2 mr-2">Attachments</b>

                    <FileUpload
                      bucket="medicareprocrm-cdn"
                      path={`public/attachments`}
                      extensions={['pdf', 'jpeg', 'png']}
                      autoUpload={true}
                      onChange={handleFileUpload}
                      reusable={true}
                      fileUploadButtonLabel={'Upload'}
                      selectButtonColor={'primary'}
                      uploadButtonColor={'primary'}
                      buttonStyle={'badge'}
                      inline={true}
                      userAccessToken={userAccessToken}
                    />
                  </div>
                  <div>
                    {Array.isArray(attachments) &&
                      attachments.length > 0 &&
                      attachments.map((item, itemIndex) => {
                        return (
                          <div
                            key={'attachment' + itemIndex}
                            className="calMess fileInstance p-2 mb-2 rounded font-weight-bold"
                            style={{ border: '2px dotted #999999', fontSize: '10pt' }}
                          >
                            <i className="far fa-file-import mr-2 "></i>
                            <span>{item.fileName}</span>
                            <span
                              className="badge badge-secondary badge-pill ml-2 cp"
                              style={{ fontSize: '8pt' }}
                              onClick={() => handleRemoveAttachment(itemIndex)}
                            >
                              <i className="far fa-times"></i> Remove
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={sendEmail}
              disabled={
                !composeMessage
                  ? !composeMessageBody || !composeMessageSubject
                  : !composeMessageBody
              }
              class="btn btn-primary mt-2"
            >
              {composeMessageSending === true ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Sending
                </>
              ) : (
                <>Send Email</>
              )}
            </button>
          </>
        )}
      </StyledModal>
      <StyledModal
        size="lg"
        title={'Save Attachment'}
        footerCloseButton={true}
        show={savingAttachment !== null}
        onClose={(e) => {
          setSavingAttachment(null);
        }}
      >
        {savingAttachment ? (
          <div className="alert small alert-info" role="alert">
            <i className="fas fa-spinner fa-spin"></i> Saving attachment to client documents
            section.
          </div>
        ) : (
          <div className="alert small alert-success" role="alert">
            Attachment saved!
          </div>
        )}
      </StyledModal>

      <div className="row email-log pt-2">
        {mailPlatform === undefined && (
          <div className="col-12">
            <div className="alert small alert-info" role="alert">
              <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
            </div>
          </div>
        )}
        {mailPlatform === null && authorizedUsers.length === 0 && (
          <div className="col-12">
            <div className="alert small alert-info" role="alert">
              No email account configured.
            </div>
            <div
              className="badge badge-pill badge-info cp mt-2"
              style={{ fontSize: '8pt' }}
              onClick={() => onIntegrationSetupClick('email')}
            >
              <i className="far fa-plus-circle"></i> Add/Edit an integration
            </div>
          </div>
        )}
        {mailPlatform === null && authorizedUsers.length > 0 && (
          <div className="col-12">
            <div className="alert small alert-info" role="alert">
              User has no email account configured.
            </div>

            <label className="label">Change Inbox</label>
            <select
              className="form-control form-control-sm mb-2"
              onChange={(e) => changeTargetUser(e.target.value)}
            >
              <option>Select...</option>
              {authorizedUsers.map((user) => {
                return (
                  <option value={user.getUser().getUserId()}>
                    {user.getUser().getUserFirstname()} {user.getUser().getUserLastname()}
                  </option>
                );
              })}
            </select>
          </div>
        )}
        {mailPlatform && (
          <>
            {mailThreadsLoading && !changingTargetUser && (
              <div className="col-12">
                <div className="alert small alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                </div>
              </div>
            )}
            {mailThreads && mailThreads.length > 0 && (
              <>
                <div className="col-3 email-log-threads">
                  <img
                    className="mb-2"
                    src={`https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/${mailPlatform}icon.png`}
                    style={{ height: '35px' }}
                  ></img>
                  <div
                    class="btn border border-primary d-block mb-2 w-100"
                    onClick={(e) => loadCompose()}
                  >
                    <i class="fal fa-plus-circle text-primary "></i>
                    <span class="d-none d-sm-inline ml-2 text-primary">New Email</span>
                  </div>
                  {authorizedUsers.length > 0 && (
                    <>
                      <label className="label">Inbox</label>
                      <select
                        className="form-control form-control-sm mb-2"
                        onChange={(e) => changeTargetUser(e.target.value)}
                      >
                        {authorizedUsers.map((user) => {
                          let selected = user.getUser().getUserId() == targetUser ? 'selected' : '';
                          return (
                            <option selected={selected} value={user.getUser().getUserId()}>
                              {user.getUser().getUserFirstname()} {user.getUser().getUserLastname()}
                            </option>
                          );
                        })}
                      </select>
                    </>
                  )}
                  {mailPlatform !== undefined &&
                    !changingTargetUser &&
                    mailThreads.map((mailThread) => {
                      let classList = [
                        'small',
                        'card',
                        'p-2',
                        'mb-2',
                        'email-log-threads-thread',
                        'cp',
                      ];

                      let dateTime = Utilities.dateTimeStringToDateTime(mailThread.dateTime);
                      let timesince = Utilities.getTimeSince(dateTime, true);

                      if (mailThread.threadId === mailThreadID) {
                        classList.push('bg-info');
                        classList.push('text-white');
                      }

                      return (
                        <div
                          onClick={(e) => loadEmail(mailThread.threadId)}
                          key={mailThread.threadId}
                          className={classList.join(' ')}
                        >
                          <div>
                            <span className="float-right small mt-1">{timesince}</span>
                            <p>
                              <strong>{mailThread.from.name || mailThread.from.email}</strong>
                            </p>
                            <p>{mailThread.subject}</p>
                            <p className={mailThread.threadId === mailThreadID ? '' : ''}>
                              {mailThread.threadPreview}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  {changingTargetUser && <i className="fas fa-spinner fa-spin ml-2"></i>}
                </div>
                <div className="col-9 email-log-messages">
                  {/* authorizedUsers.length > 0 && 
                                            <div class="mb-2">
                                                <div className="badge badge-primary dataBadge" style={{fontSize:'10pt'}}><i className="far fa-inbox"></i> {targetUserName}</div>
                                            </div> */}

                  {mailMessagesLoading === true && (
                    <div className="alert small alert-info" role="alert">
                      <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                    </div>
                  )}
                  {mailMessages &&
                    !changingTargetUser &&
                    mailMessages.map((mailMessage) => {
                      let classList = ['card', 'p-2', 'mb-2', 'bg-white'];

                      if (mailMessage.direction === 'S') {
                        classList.push('direction-s');
                      } else {
                        classList.push('direction-r');
                      }

                      let dateTime = Utilities.dateTimeStringToDateTime(mailMessage.dateTime);
                      let timesince = Utilities.getTimeSince(dateTime);

                      return (
                        <div className="email-log-messages-message">
                          <div
                            className={classList.join(' ')}
                            onClick={(e) =>
                              setMailMessageID(
                                mailMessageID === mailMessage.id ? null : mailMessage.id
                              )
                            }
                          >
                            <div>
                              <span className="float-right small mt-1">
                                {timesince} {dateTime.toLocaleString()}
                              </span>
                              <p>
                                <strong>{mailMessage.from.name || mailMessage.from.email}</strong>
                              </p>
                              {mailMessageID === mailMessage.id || mailMessages.length === 1 ? (
                                <>
                                  {mailMessage.attachments &&
                                    mailMessage.attachments.length > 0 && (
                                      <div className="mt-1">
                                        {mailMessage.attachments.map((attachment) => {
                                          return (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                saveAttachment(mailMessage.id, attachment.id);
                                              }}
                                              className="btn btn-sm btn-outline-info border border-info mr-1"
                                            >
                                              {attachment.name} <i class="far fa-save ml-1"></i>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}

                                  <iframe
                                    onLoad={(e) =>
                                      (e.currentTarget.style.height =
                                        e.currentTarget.contentWindow.document.documentElement
                                          .scrollHeight + 'px')
                                    }
                                    className="mt-2 rounded"
                                    srcdoc={mailMessage.message}
                                  ></iframe>
                                  <div
                                    className="btn btn-sm border border-primary d-inline-block"
                                    onClick={(e) => loadCompose(mailMessage, mailMessage.subject)}
                                  >
                                    Reply
                                  </div>
                                </>
                              ) : (
                                <p>{mailMessage.messagePreview}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </>
            )}
            {emailAddresses && emailAddresses.length === 0 && (
              <div className="col-12">
                <div className="alert small alert-info" role="alert">
                  Client has no email address.
                </div>
              </div>
            )}
            {mailThreadsErrorMessage && (
              <div className="col-12">
                <div className="alert small alert-warning" role="alert">
                  {mailThreadsErrorMessage}
                </div>
                <div
                  className="badge badge-pill badge-info cp mt-2"
                  style={{ fontSize: '8pt' }}
                  onClick={() => onIntegrationSetupClick('email')}
                >
                  <i className="far fa-plus-circle"></i> Troubleshoot integration
                </div>
              </div>
            )}
            {!mailThreadsErrorMessage &&
              !mailThreadsLoading &&
              !mailThreads &&
              emailAddresses &&
              emailAddresses.length > 0 && (
                <div className="col-12">
                  <div className="alert small alert-info" role="alert">
                    No emails to show.{' '}
                    <a
                      href=""
                      onClick={(e) => {
                        e.preventDefault();
                        loadCompose();
                      }}
                    >
                      Click here
                    </a>{' '}
                    to compose a new email.
                  </div>

                  {authorizedUsers.length > 0 && (
                    <>
                      <label className="label">Change Inbox</label>
                      <select
                        className="form-control form-control-sm mb-2"
                        onChange={(e) => changeTargetUser(e.target.value)}
                      >
                        {authorizedUsers.map((user) => {
                          let selected = user.getUser().getUserId() == targetUser ? 'selected' : '';
                          return (
                            <option selected={selected} value={user.getUser().getUserId()}>
                              {user.getUser().getUserFirstname()} {user.getUser().getUserLastname()}
                            </option>
                          );
                        })}
                      </select>
                    </>
                  )}
                </div>
              )}
          </>
        )}
      </div>
    </>
  );
}
